/**
 * Générateur — Attendu 964 — « Construire des parallélogrammes. » (libellé officiel, BO)
 *
 * Niveau : 5ème | Domaine : Espace et géométrie / Parallélogrammes
 * Mise en conformité avec la spécification `Attendu-5e/specifications-tanoed/5e/964.json`
 * (campagne 5e, 2026-09-21). La spécification est la seule référence.
 *
 * ── Plan de cartes (version finale) ──────────────────────────────────────────
 *
 *  Les quatre jeux de données de la spec, un par niveau, chacun appelant une
 *  propriété caractéristique différente (`jeux_de_donnees_retenus`,
 *  `objets_geometriques` 1) :
 *
 *  1. definition — niveau 1 : deux côtés consécutifs tracés, placer le
 *                  quatrième sommet — côtés opposés PARALLÈLES.
 *  2. pointe     — niveau 2 : trois sommets consécutifs marqués sur papier
 *                  pointé, sans les côtés — côtés opposés de MÊME LONGUEUR.
 *  3. propriete  — niveau 2 aux instruments : mêmes données, report de
 *                  longueur au compas, les quatre côtés à tracer.
 *  4. centre     — niveau 3 : deux sommets consécutifs et le centre — les
 *                  DIAGONALES se coupent en leur milieu.
 *  5. angle      — niveau 4 : un angle donné par deux demi-droites et le sommet
 *                  opposé, aucune longueur fournie — SYMÉTRIE CENTRALE.
 *  6. codage     — réglage transverse : le codage de la figure, question
 *                  séparée (`type_reponse`, `ecarts_assumes` 2).
 *  7. methode    — transverse : ce qui fait qu'une construction en est une
 *                  (V/F portant les erreurs visées 2, 4, 5, 6 et 8).
 *  Niveau 5 (mélange) : `generate()` sans clé.
 *
 * ── Spécification ────────────────────────────────────────────────────────────
 *
 *  Bornes appliquées :
 *   - longueur d'un côté : entier de 4 à 8 cm sur les constructions aux
 *     instruments (1 cm = 1 carreau de la grille du composant) ;
 *   - rapport des deux côtés consécutifs : de 0,6 à 0,9 — huit couples
 *     possibles, jamais deux côtés égaux ;
 *   - angle au sommet : entier de 30 à 70 degrés, jamais droit ;
 *   - orientation de la figure : entier de 0 à 359 degrés, mais jamais une
 *     orientation qui rendrait un côté horizontal (exclusion 4, contrôlée à
 *     5 degrés près) ;
 *   - nombre de sommets à construire : 1 (niveaux 1 et 2), 2 (niveau 3),
 *     3 (niveau 4) — dans [1 ; 3] ;
 *   - lettres O, Q et I écartées du nommage des sommets ; O réservée au centre.
 *  Exclusions : aucun rectangle, aucun losange, aucun carré (l'angle n'est
 *   jamais droit, les côtés consécutifs jamais égaux, les diagonales jamais de
 *   même longueur ni perpendiculaires — vérifié tirage par tirage) ; aucun
 *   quadrilatère croisé ni aplati (construit par deux vecteurs non colinéaires,
 *   les diagonales se coupent toujours à l'intérieur) ; aucun côté horizontal ;
 *   les données de l'énoncé ne sont ni déplaçables ni supprimables (le composant
 *   ne le permet pas).
 *
 *  Erreurs visées → mots-clés (codes des distracteurs, `_meta.params`) :
 *   1. tracer à vue un quadrilatère qui ressemble à un parallélogramme
 *      → `trace_a_vue` (`erreursVisees` des constructions : chaque sommet est
 *        comparé à sa position, la tolérance est annoncée ; V/F)
 *   2. reporter une longueur au lieu de la doubler quand le centre est donné
 *      → `report_au_lieu_de_doubler` (methode_tableau_vf ; `erreursVisees` de
 *        centre)
 *   3. prendre le milieu d'un côté pour le milieu d'une diagonale
 *      → `milieu_de_cote` (methode_tableau_vf, codage_qcm_unique)
 *   4. négliger que l'écriture ABCD impose l'ordre de parcours des sommets
 *      → `ordre_des_lettres` (methode_tableau_vf ; dit dans chaque énoncé)
 *   5. construire les quatre sommets sans jamais tracer la figure
 *      → `figure_non_tracee` (methode_tableau_vf ; les côtés sont des tâches
 *        distinctes des sommets, `type_reponse` « un retour distinct pour
 *        chacun de ces deux critères »)
 *   6. chercher à reporter des longueurs quand aucune n'est donnée
 *      → `report_sans_longueur` (methode_tableau_vf ; `erreursVisees` de angle)
 *   7. coder la figure avec des marques qui ne correspondent pas à la propriété
 *      employée → `codage_incoherent` (codage_qcm_unique)
 *   8. croire qu'il n'existe qu'une seule construction possible
 *      → `construction_unique` (methode_tableau_vf ; chaque énoncé de
 *        construction dit qu'il existe une seconde solution et laquelle est
 *        attendue, `ecarts_assumes` 1, vigilance 6)
 *
 *  Graduation → cartes : 1 definition · 2 pointe et propriete · 3 centre ·
 *   4 angle · 5 `generate()` sans clé ; transverses : codage, methode.
 *
 *  Non tranché (appliqué tel qu'écrit, signalé dans le rapport) :
 *   - La grille d'auto-contrôle 5 × 5 cases repérées par une lettre et un
 *     chiffre n'a pas de format dans l'application : le régime servi est celui
 *     de la construction, où la tolérance est de 12 px, soit 0,4 carreau, du
 *     même ordre que la case de 4 à 8 mm de la spec, et annoncée à l'élève.
 *   - Sur papier pointé, les côtés n'ont pas de longueur entière en
 *     centimètres : `longueur_d_un_cote` ne s'y applique pas, seuls le rapport
 *     des côtés et l'angle au sommet y sont contrôlés.
 *   - Les arcs de compas ne sont pas des tâches : `objets_geometriques` 4 les
 *     demande sur la figure de CORRECTION, où ils apparaissent si l'élève les
 *     a tracés.
 */

import {
    randInt, randomChoice, shuffle, pickLettresSains,
    basePayload, makeQcm, makeVraiFaux, makeProgrammeConstruction,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 964;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });
const meta = (payload, key, params) => {
    payload._meta.generatorKey = key;
    payload._meta.params = params;
    return payload;
};

const L_DEFINITION = 'Construire par la définition';
const L_POINTE = 'Compléter sur papier pointé';
const L_PROPRIETE = 'Construire par la propriété';
const L_CENTRE = 'Construire par le centre';
const L_ANGLE = 'Construire par la symétrie centrale';
const L_CODAGE = 'Coder la figure construite';
const L_METHODE = 'Ce qui fait une construction';

const COTE_MIN = 4;
const COTE_MAX = 8;
const RAPPORT_MIN = 0.6;
const RAPPORT_MAX = 0.9;
const ANGLE_MIN = 30;
const ANGLE_MAX = 70;

const PX = 30;
const TOL = 12;
const TOL_TXT = '0,4 carreau';
const PAD = 56;
const COL_V = '#1D9E75';
/** Bleu des TRACES de construction : les arcs de compas, qui restent sur la figure. */
const COL_B = '#1E3A8A';

const toRad = (d) => (d * Math.PI) / 180;
/** Distance entre deux points de la figure, en pixels — le rayon d'un report. */
const dist = (P, Q) => Math.hypot(Q.x - P.x, Q.y - P.y);
// Les lettres O, Q et I sont écartées (exclusion 3) : O est réservée au centre.
const POOL_LETTRES = 'ABCDEFGHJKLMNPRSTUVWXYZ'.split('');
const pickLetters = (n) => pickLettresSains(POOL_LETTRES, n);
const NOM_CENTRE = 'O';

const E_VUE = 'trace_a_vue';
const E_REPORT_DOUBLE = 'report_au_lieu_de_doubler';
const E_MILIEU_COTE = 'milieu_de_cote';
const E_ORDRE = 'ordre_des_lettres';
const E_NON_TRACEE = 'figure_non_tracee';
const E_REPORT_SANS = 'report_sans_longueur';
const E_CODAGE = 'codage_incoherent';
const E_UNIQUE = 'construction_unique';

/** Couples (grand côté, petit côté) entiers de 4 à 8 cm, de rapport 0,6 à 0,9. */
const COUPLES_COTES = (() => {
    const out = [];
    for (let a = COTE_MIN; a <= COTE_MAX; a++) {
        for (let b = COTE_MIN; b <= COTE_MAX; b++) {
            const r = b / a;
            if (r >= RAPPORT_MIN - 1e-9 && r <= RAPPORT_MAX + 1e-9) out.push([a, b]);
        }
    }
    return out;
})();

/** Une direction est-elle assez loin de l'horizontale ? (exclusion 4) */
const nonHorizontale = (deg) => {
    const m = ((deg % 180) + 180) % 180;
    return m > 5 && m < 175;
};

/**
 * Parallélogramme ABCD exact, en pixels : A au point de départ, u = A→B de
 * longueur `c1`, v = A→D de longueur `c2`, angle `alpha` entre les deux,
 * orientation `theta`. Aucun côté horizontal, jamais losange (c1 ≠ c2), jamais
 * rectangle (alpha ≠ 90), donc jamais carré (exclusion 1).
 */
const tirerParallelogramme = () => {
    const [c1, c2] = randomChoice(COUPLES_COTES);
    const alpha = randInt(ANGLE_MIN, ANGLE_MAX);
    let theta = randInt(0, 359);
    for (let i = 0; i < 400 && !(nonHorizontale(theta) && nonHorizontale(theta + alpha)); i++) {
        theta = randInt(0, 359);
    }
    if (!(nonHorizontale(theta) && nonHorizontale(theta + alpha))) theta = 35;
    const A = { x: 0, y: 0 };
    const u = { x: c1 * PX * Math.cos(toRad(theta)), y: -c1 * PX * Math.sin(toRad(theta)) };
    const v = { x: c2 * PX * Math.cos(toRad(theta + alpha)), y: -c2 * PX * Math.sin(toRad(theta + alpha)) };
    const B = { x: A.x + u.x, y: A.y + u.y };
    const C = { x: A.x + u.x + v.x, y: A.y + u.y + v.y };
    const D = { x: A.x + v.x, y: A.y + v.y };
    const O = { x: (A.x + C.x) / 2, y: (A.y + C.y) / 2 };
    return { A, B, C, D, O, c1, c2, alpha, theta };
};

const paramsPara = (p, niveau, extra) => ({
    niveau,
    longueur_d_un_cote: [p.c1, p.c2],
    rapport_des_deux_cotes_consecutifs: Math.round((p.c2 / p.c1) * 1000) / 1000,
    angle_au_sommet: p.alpha,
    orientation_de_la_figure: p.theta,
    diagonales_de_meme_longueur: false,
    diagonales_perpendiculaires: false,
    ...extra,
});

// ═══════════════════════════════════════════════════════════
// ASSEMBLAGE D'UN PROGRAMME DE CONSTRUCTION
// ═══════════════════════════════════════════════════════════

const assembler = ({ points, preShapes, tasks, texte, label, key, params }) => {
    const xs = [];
    const ys = [];
    const noter = (p) => { xs.push(p.x); ys.push(p.y); };
    points.forEach(noter);
    tasks.forEach((t) => {
        const s = t.shape;
        if (s.type === 'segment' || s.type === 'line') { noter(s.p1); noter(s.p2); }
        else { xs.push(s.cx - s.r, s.cx + s.r); ys.push(s.cy - s.r, s.cy + s.r); }
    });
    // Un `preShape` peut viser un point libre, donné en tableau [x, y]
    // (ProgrammeConstructionEleve l'accepte à la place d'un nom de point).
    (preShapes || []).forEach((s) => {
        if (Array.isArray(s.from)) noter({ x: s.from[0], y: s.from[1] });
        if (Array.isArray(s.to)) noter({ x: s.to[0], y: s.to[1] });
    });
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const dx = PAD - minX;
    const dy = PAD - minY;
    const vb = {
        w: Math.round(Math.max(...xs) - minX + 2 * PAD),
        h: Math.round(Math.max(...ys) - minY + 2 * PAD),
    };
    const a2 = (v) => Math.round(v * 100) / 100;
    const mv = (p) => ({ ...p, x: a2(p.x + dx), y: a2(p.y + dy) });
    const mvShape = (s) => ((s.type === 'segment' || s.type === 'line')
        ? { ...s, p1: mv(s.p1), p2: mv(s.p2) }
        : { ...s, cx: a2(s.cx + dx), cy: a2(s.cy + dy) });
    const mvPre = (s) => ({
        ...s,
        ...(Array.isArray(s.from) ? { from: [a2(s.from[0] + dx), a2(s.from[1] + dy)] } : {}),
        ...(Array.isArray(s.to) ? { to: [a2(s.to[0] + dx), a2(s.to[1] + dy)] } : {}),
    });
    const payload = makeProgrammeConstruction({
        pxPerCm: PX,
        tolerance: TOL,
        viewBox: vb,
        points: points.map(mv),
        preShapes: (preShapes || []).map(mvPre),
        tasks: tasks.map((t) => ({ ...t, shape: mvShape(t.shape) })),
    }, texte, opts(label));
    return meta(payload, key, params);
};

const tachePoint = (id, P, nom, consigne) => ({
    id, consigne, color: COL_V, pointLabel: nom,
    shape: { type: 'circle', cx: P.x, cy: P.y, r: 0 },
});
/**
 * Arc de compas — un report de longueur, que l'eleve TRACE.
 *
 * Sans lui, le sommet a construire etait une cible de rayon nul : l'eleve
 * cliquait ou il pensait, la tolerance tranchait, et le compas annonce dans la
 * consigne ne devenait jamais un geste (retour prof 2026-09-26). La spec 964
 * l'interdit deux fois : « jamais par un trace approche », et « traces de
 * construction conservees en correction : arcs de compas… c'est la methode qui
 * doit rester lisible sur la figure corrigee, pas seulement le resultat ».
 */
const tacheArc = (id, C, rPx, consigne) => ({
    id, consigne, color: COL_B,
    shape: { type: 'circle', cx: C.x, cy: C.y, r: rPx },
});

const tacheCote = (id, P, Q, nP, nQ) => ({
    id, kind: 'segment', color: COL_V,
    consigne: `Trace le côté [${nP}${nQ}].`,
    shape: { type: 'segment', p1: { x: P.x, y: P.y }, p2: { x: Q.x, y: Q.y } },
});

/**
 * Phrase commune : la tolérance annoncée (vigilance 1), l'ordre de parcours des
 * lettres (erreur visée 4) et l'existence d'une seconde solution (erreur visée 8,
 * `ecarts_assumes` 1, vigilance 6 — l'ambiguïté est levée dans la consigne, pas
 * dans le code).
 */
const pied = (nA, nB, nC, nD) => `<p><em>L'écriture ${nA}${nB}${nC}${nD} impose l'ordre de parcours des `
    + `sommets : le côté [${nB}${nC}] existe, le côté [${nB}${nD}] non, c'est une diagonale. `
    + `Chaque tracé est juste si ses extrémités tombent à moins de ${TOL_TXT} de leur position ; les `
    + 'sommets et les côtés sont comptés séparément.</em></p>';

// ═══════════════════════════════════════════════════════════
// Carte 1 — definition (niveau 1) : deux côtés consécutifs tracés
// ═══════════════════════════════════════════════════════════

const genDefinitionPC = () => {
    const p = tirerParallelogramme();
    const [nA, nB, nC, nD] = pickLetters(4);
    return assembler({
        points: [{ name: nA, ...p.A }, { name: nB, ...p.B }, { name: nD, ...p.D }],
        preShapes: [{ type: 'line', from: nA, to: nB }, { type: 'line', from: nA, to: nD }],
        tasks: [
            tachePoint('point_C', p.C, nC,
                `Place le sommet ${nC}, à l'intersection de la parallèle à (${nA}${nB}) passant par ${nD} et de la parallèle à (${nA}${nD}) passant par ${nB}.`),
            tacheCote('cote_BC', p.B, p.C, nB, nC),
            tacheCote('cote_DC', p.D, p.C, nD, nC),
        ],
        texte: `<p>Les côtés [${nA}${nB}] et [${nA}${nD}] du parallélogramme ${nA}${nB}${nC}${nD} sont `
            + `déjà tracés. Place le quatrième sommet ${nC}, puis trace les deux côtés qui manquent.</p>`
            + `<p>Propriété employée : dans un parallélogramme, les côtés opposés sont <b>parallèles</b>. `
            + `Instruments : règle et équerre.</p>` + pied(nA, nB, nC, nD),
        label: L_DEFINITION,
        key: 'definition_programme_construction',
        params: paramsPara(p, 1, {
            jeu_de_donnees: 'deux cotes consecutifs traces',
            propriete: 'cotes opposes paralleles',
            nombre_de_sommets_a_construire: 1,
            erreursVisees: [E_VUE, E_ORDRE],
        }),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — pointe (niveau 2) : papier pointé
// ═══════════════════════════════════════════════════════════

const REPERE = { xMin: 0, yMin: 0, xMax: 10, yMax: 10, xStep: 1, yStep: 1 };

/**
 * Couples de vecteurs entiers (u, v) d'un parallélogramme sur papier pointé :
 * aucun côté horizontal, rapport des longueurs de 0,6 à 0,9 et angle au sommet
 * de 30 à 70 degrés — donc jamais losange, jamais rectangle, jamais carré
 * (exclusion 1). Calculé une fois au chargement du module.
 */
const VECTEURS_POINTES = (() => {
    const base = [];
    for (let x = -6; x <= 6; x++) {
        for (let y = -6; y <= 6; y++) {
            if (y === 0) continue;                 // aucun côté horizontal
            const n = Math.hypot(x, y);
            if (n >= 3 && n <= 6.5) base.push({ x, y, n });
        }
    }
    const out = [];
    base.forEach((u) => base.forEach((v) => {
        const det = u.x * v.y - u.y * v.x;
        if (det <= 0) return;                      // ordre direct, non colinéaires
        const r = v.n / u.n;
        if (r < RAPPORT_MIN || r > RAPPORT_MAX) return;
        const cos = (u.x * v.x + u.y * v.y) / (u.n * v.n);
        const a = (Math.acos(Math.max(-1, Math.min(1, cos))) * 180) / Math.PI;
        if (a < ANGLE_MIN || a > ANGLE_MAX) return;
        out.push({ u: { x: u.x, y: u.y }, v: { x: v.x, y: v.y }, rapport: r, alpha: a });
    }));
    return out;
})();

const tirerPointe = () => {
    for (let i = 0; i < 300; i++) {
        const { u, v, rapport, alpha } = randomChoice(VECTEURS_POINTES);
        const A = { x: randInt(1, 9), y: randInt(1, 9) };
        const B = { x: A.x + u.x, y: A.y + u.y };
        const C = { x: A.x + u.x + v.x, y: A.y + u.y + v.y };
        const D = { x: A.x + v.x, y: A.y + v.y };
        if ([A, B, C, D].every((q) => q.x >= 1 && q.x <= 9 && q.y >= 1 && q.y <= 9)) {
            return { A, B, C, D, rapport, alpha };
        }
    }
    const f = VECTEURS_POINTES[0];
    const A = { x: 2, y: 2 };
    return {
        A,
        B: { x: A.x + f.u.x, y: A.y + f.u.y },
        C: { x: A.x + f.u.x + f.v.x, y: A.y + f.u.y + f.v.y },
        D: { x: A.x + f.v.x, y: A.y + f.v.y },
        rapport: f.rapport,
        alpha: f.alpha,
    };
};

const genPointe = () => {
    const t = tirerPointe();
    const [nA, nB, nC, nD] = pickLetters(4);
    const texte = `<p>Sur ce papier pointé, trois sommets consécutifs du parallélogramme `
        + `${nA}${nB}${nC}${nD} sont marqués, sans les côtés. Place le quatrième sommet <b>${nD}</b>.</p>`
        + `<p>Propriété employée : les côtés opposés ont la <b>même longueur</b> et la même direction, `
        + `donc on passe de ${nB} à ${nA} comme de ${nC} à ${nD}.</p>`
        + `<p><em>L'écriture ${nA}${nB}${nC}${nD} impose l'ordre de parcours des sommets.</em></p>`;
    const payload = basePayload('association', texte, {
        subType: 'placer_points_2d',
        repere: REPERE,
        pointsToPlace: [nD],
        dejaPlaces: [
            { x: t.A.x, y: t.A.y, label: nA },
            { x: t.B.x, y: t.B.y, label: nB },
            { x: t.C.x, y: t.C.y, label: nC },
        ],
        bonne_reponse: JSON.stringify({ [nD]: `${t.D.x};${t.D.y}` }),
    }, opts(L_POINTE));
    return meta(payload, 'pointe_placer_points_2d', {
        niveau: 2,
        jeu_de_donnees: 'trois sommets consecutifs marques',
        propriete: 'cotes opposes de meme longueur',
        rapport_des_deux_cotes_consecutifs: Math.round(t.rapport * 1000) / 1000,
        angle_au_sommet: Math.round(t.alpha),
        nombre_de_sommets_a_construire: 1,
        diagonales_de_meme_longueur: false,
        diagonales_perpendiculaires: false,
        erreursVisees: [E_VUE, E_ORDRE],
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — propriete (niveau 2, aux instruments)
// ═══════════════════════════════════════════════════════════

const genProprietePC = () => {
    const p = tirerParallelogramme();
    const [nA, nB, nC, nD] = pickLetters(4);
    return assembler({
        points: [{ name: nA, ...p.A }, { name: nB, ...p.B }, { name: nC, ...p.C }],
        preShapes: [],
        tasks: [
            tacheCote('cote_AB', p.A, p.B, nA, nB),
            tacheCote('cote_BC', p.B, p.C, nB, nC),
            tacheArc('arc_A', p.A, dist(p.B, p.C),
                `Reporte la longueur ${nB}${nC} depuis ${nA} : trace l'arc de centre ${nA} et de rayon ${nB}${nC}.`),
            tacheArc('arc_C', p.C, dist(p.A, p.B),
                `Reporte la longueur ${nA}${nB} depuis ${nC} : trace l'arc de centre ${nC} et de rayon ${nA}${nB}.`),
            tachePoint('point_D', p.D, nD,
                `Place le sommet ${nD} à l'intersection des deux arcs.`),
            tacheCote('cote_CD', p.C, p.D, nC, nD),
            tacheCote('cote_DA', p.D, p.A, nD, nA),
        ],
        texte: `<p>Trois sommets consécutifs du parallélogramme ${nA}${nB}${nC}${nD} sont marqués, `
            + `sans les côtés. Relie-les, place le quatrième sommet ${nD}, puis ferme la figure.</p>`
            + `<p>Propriété employée : les côtés opposés ont la <b>même longueur</b>, `
            + `donc ${nA}${nD} = ${nB}${nC} et ${nC}${nD} = ${nA}${nB}. Instruments : règle et compas.</p>`
            + `<p>Les deux arcs restent sur la figure : ce sont eux qui montrent par quelle `
            + 'méthode le sommet a été trouvé.</p>'
            + `<p>Les deux arcs se coupent en <b>deux</b> points : prends celui qui donne le `
            + `parallélogramme ${nA}${nB}${nC}${nD} dans cet ordre.</p>` + pied(nA, nB, nC, nD),
        label: L_PROPRIETE,
        key: 'propriete_programme_construction',
        params: paramsPara(p, 2, {
            jeu_de_donnees: 'trois sommets consecutifs marques',
            propriete: 'cotes opposes de meme longueur',
            nombre_de_sommets_a_construire: 1,
            traces_de_construction: ['arc_A', 'arc_C'],
            erreursVisees: [E_VUE, E_UNIQUE, E_NON_TRACEE],
        }),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — centre (niveau 3) : deux sommets et le centre
// ═══════════════════════════════════════════════════════════

const genCentrePC = () => {
    const p = tirerParallelogramme();
    const [nA, nB, nC, nD] = pickLetters(4);
    return assembler({
        points: [{ name: nA, ...p.A }, { name: nB, ...p.B }, { name: NOM_CENTRE, ...p.O }],
        preShapes: [{ type: 'line', from: nA, to: nB }],
        tasks: [
            tachePoint('point_C', p.C, nC,
                `Place le sommet ${nC} : prolonge [${nA}${NOM_CENTRE}) au-delà de ${NOM_CENTRE} et reporte la longueur ${nA}${NOM_CENTRE}. ${nC} est le symétrique de ${nA} par rapport à ${NOM_CENTRE}.`),
            tachePoint('point_D', p.D, nD,
                `Place de même le sommet ${nD}, symétrique de ${nB} par rapport à ${NOM_CENTRE}.`),
            tacheCote('cote_BC', p.B, p.C, nB, nC),
            tacheCote('cote_CD', p.C, p.D, nC, nD),
            tacheCote('cote_DA', p.D, p.A, nD, nA),
        ],
        texte: `<p>${nA} et ${nB} sont deux sommets consécutifs du parallélogramme `
            + `${nA}${nB}${nC}${nD}, et ${NOM_CENTRE} en est le centre. Construis les deux sommets `
            + `qui manquent, puis trace les côtés.</p>`
            + `<p>Propriété employée : les <b>diagonales</b> d'un parallélogramme se coupent en leur `
            + `<b>milieu</b>. ${NOM_CENTRE} est donc le milieu de [${nA}${nC}] et de [${nB}${nD}] : la `
            + `longueur ${nA}${NOM_CENTRE} est à <b>doubler</b>, pas à reporter une seule fois. `
            + `Instruments : règle graduée.</p>` + pied(nA, nB, nC, nD),
        label: L_CENTRE,
        key: 'centre_programme_construction',
        params: paramsPara(p, 3, {
            jeu_de_donnees: 'deux sommets consecutifs et le centre',
            propriete: 'diagonales se coupant en leur milieu',
            nombre_de_sommets_a_construire: 2,
            erreursVisees: [E_REPORT_DOUBLE, E_MILIEU_COTE],
        }),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — angle (niveau 4) : symétrie centrale, aucune longueur
// ═══════════════════════════════════════════════════════════

const genAnglePC = () => {
    const p = tirerParallelogramme();
    const [nA, nB, nC, nD, nX, nY] = pickLetters(6);
    // Les deux demi-droites issues de A dépassent les sommets B et D et portent
    // chacune un point nommé : l'élève ne peut lire aucune longueur sur la figure
    // (aucune longueur fournie), mais il peut désigner sans ambiguïté chaque
    // demi-droite (vigilance 6 : l'ambiguïté se lève dans la consigne).
    const prolonge = (P, Q, k) => ({ x: P.x + k * (Q.x - P.x), y: P.y + k * (Q.y - P.y) });
    const X = prolonge(p.A, p.B, 1.35);
    const Y = prolonge(p.A, p.D, 1.45);
    return assembler({
        points: [{ name: nA, ...p.A }, { name: nC, ...p.C }, { name: nX, ...X }, { name: nY, ...Y }],
        preShapes: [
            { type: 'line', from: nA, to: nX },
            { type: 'line', from: nA, to: nY },
        ],
        tasks: [
            tachePoint('point_O', p.O, NOM_CENTRE,
                `Place le centre ${NOM_CENTRE} du parallélogramme : c'est le milieu de [${nA}${nC}].`),
            tachePoint('point_B', p.B, nB,
                `Place ${nB} sur la demi-droite [${nA}${nX}) : c'est le point où la parallèle à (${nA}${nY}) passant par ${nC} la rencontre.`),
            tachePoint('point_D', p.D, nD,
                `Place ${nD} sur la demi-droite [${nA}${nY}), de la même façon. ${nD} est aussi le symétrique de ${nB} par rapport à ${NOM_CENTRE}.`),
            tacheCote('cote_BC', p.B, p.C, nB, nC),
            tacheCote('cote_DC', p.D, p.C, nD, nC),
        ],
        texte: `<p>L'angle de sommet ${nA} du parallélogramme ${nA}${nB}${nC}${nD} est donné par ses `
            + `deux demi-droites [${nA}${nX}) et [${nA}${nY}), et le sommet opposé ${nC} est marqué. <b>Aucune longueur n'est `
            + `fournie</b> : il n'y a rien à reporter au compas. Construis ${NOM_CENTRE}, ${nB} et `
            + `${nD}, puis trace les deux côtés qui manquent.</p>`
            + `<p>Propriété employée : le parallélogramme est sa propre image par la <b>symétrie `
            + `centrale</b> de centre ${NOM_CENTRE}, milieu commun des deux diagonales. `
            + 'Instruments : règle et équerre.</p>' + pied(nA, nB, nC, nD),
        label: L_ANGLE,
        key: 'angle_programme_construction',
        params: paramsPara(p, 4, {
            jeu_de_donnees: 'un angle et le sommet oppose',
            propriete: 'symetrie centrale',
            nombre_de_sommets_a_construire: 3,
            erreursVisees: [E_REPORT_SANS, E_VUE],
        }),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 6 — codage (question séparée)
// ═══════════════════════════════════════════════════════════

const METHODES_CODAGE = [
    {
        id: 'longueurs',
        phrase: 'en reportant au compas les longueurs des côtés opposés',
        bon: (n) => `Les mêmes marques d'égalité sur [${n[0]}${n[1]}] et [${n[3]}${n[2]}], et d'autres marques identiques sur [${n[0]}${n[3]}] et [${n[1]}${n[2]}].`,
    },
    {
        id: 'diagonales',
        phrase: 'en plaçant le centre, milieu commun des deux diagonales',
        bon: (n) => `Les mêmes marques d'égalité sur [${n[0]}${NOM_CENTRE}] et [${NOM_CENTRE}${n[2]}], et d'autres marques identiques sur [${n[1]}${NOM_CENTRE}] et [${NOM_CENTRE}${n[3]}].`,
    },
    {
        id: 'paralleles',
        phrase: 'en traçant les parallèles aux deux côtés déjà tracés',
        bon: (n) => `Des flèches de parallélisme sur [${n[0]}${n[1]}] et [${n[3]}${n[2]}], et d'autres flèches sur [${n[0]}${n[3]}] et [${n[1]}${n[2]}].`,
    },
];

const genCodageQcm = () => {
    const n = pickLetters(4);
    const m = randomChoice(METHODES_CODAGE);
    const autres = METHODES_CODAGE.filter((x) => x.id !== m.id);
    const options = [
        { choix: m.bon(n), code: null },
        { choix: autres[0].bon(n), code: E_CODAGE },
        { choix: autres[1].bon(n), code: E_CODAGE },
        {
            choix: `Les mêmes marques d'égalité sur les quatre côtés [${n[0]}${n[1]}], [${n[1]}${n[2]}], [${n[2]}${n[3]}] et [${n[3]}${n[0]}].`,
            code: E_MILIEU_COTE,
        },
    ];
    const texte = `<p>On a construit le parallélogramme ${n.join('')}, de centre ${NOM_CENTRE}, `
        + `${m.phrase}. Quel codage faut-il porter sur la figure pour faire comprendre la méthode `
        + 'employée ?</p>';
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, opts(L_CODAGE));
    return meta(payload, 'codage_qcm_unique', {
        niveau: 2,
        methode_codee: m.id,
        distracteurs: options.slice(1),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 7 — methode (V/F transverse)
// ═══════════════════════════════════════════════════════════

const VF_LIGNES = [
    { t: 'Un parallélogramme peut se construire de plusieurs façons différentes.', v: 0, code: null },
    { t: 'Les quatre sommets placés, il reste à tracer les quatre côtés pour que la figure existe.', v: 0, code: null },
    { t: 'Dans l\'écriture ABCD, l\'ordre des lettres dit dans quel ordre les sommets se suivent.', v: 0, code: null },
    { t: 'Quand le centre est donné, on peut construire le parallélogramme sans qu\'aucune longueur soit écrite dans l\'énoncé.', v: 0, code: null },
    { t: 'Quand le centre O et le sommet A sont donnés, on reporte la longueur AO une seule fois pour obtenir le sommet opposé.', v: 1, code: E_REPORT_DOUBLE },
    { t: 'Le centre d\'un parallélogramme est le milieu d\'un de ses côtés.', v: 1, code: E_MILIEU_COTE },
    { t: 'ABCD et ABDC désignent le même quadrilatère.', v: 1, code: E_ORDRE },
    { t: 'Une fois les quatre sommets placés, la figure est construite : tracer les côtés est facultatif.', v: 1, code: E_NON_TRACEE },
    { t: 'Sans aucune longueur donnée, il est impossible de construire un parallélogramme.', v: 1, code: E_REPORT_SANS },
    { t: 'Trois sommets étant donnés, il n\'existe qu\'un seul parallélogramme possible, quel que soit l\'ordre des lettres.', v: 1, code: E_UNIQUE },
];

const genMethodeVf = () => {
    const vrais = shuffle(VF_LIGNES.filter((l) => l.v === 0)).slice(0, 2);
    const faux = shuffle(VF_LIGNES.filter((l) => l.v === 1)).slice(0, 3);
    const lignes = shuffle([...vrais, ...faux]);
    const texte = '<p>Pour chaque affirmation sur la construction d\'un parallélogramme, indique si '
        + 'elle est vraie ou fausse.</p>';
    const payload = makeVraiFaux(lignes.map((x) => x.t), lignes.map((x) => x.v), texte, opts(L_METHODE));
    return meta(payload, 'methode_tableau_vf', {
        niveau: 5,
        distracteurs: faux.map((l) => ({ choix: l.t, code: l.code })),
        erreursVisees: [E_VUE],
    });
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    definition_programme_construction: genDefinitionPC,
    pointe_placer_points_2d: genPointe,
    propriete_programme_construction: genProprietePC,
    centre_programme_construction: genCentrePC,
    angle_programme_construction: genAnglePC,
    codage_qcm_unique: genCodageQcm,
    methode_tableau_vf: genMethodeVf,
};

const FORMAT_LABELS = {
    placer_points_2d: 'Repère', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux',
    programme_construction: 'Construction',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'definition', label: L_DEFINITION, formatsList: buildFormats('definition') },
    { id: 'pointe', label: L_POINTE, formatsList: buildFormats('pointe') },
    { id: 'propriete', label: L_PROPRIETE, formatsList: buildFormats('propriete') },
    { id: 'centre', label: L_CENTRE, formatsList: buildFormats('centre') },
    { id: 'angle', label: L_ANGLE, formatsList: buildFormats('angle') },
    { id: 'codage', label: L_CODAGE, formatsList: buildFormats('codage') },
    { id: 'methode', label: L_METHODE, formatsList: buildFormats('methode') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    COUPLES_COTES, VECTEURS_POINTES, VF_LIGNES, METHODES_CODAGE,
    tirerParallelogramme, tirerPointe, nonHorizontale,
};
